import { Alert, type FormControlProps, FormHelperText, Grid } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useSetAtom } from "jotai";
import { uniqBy } from "lodash-es";
import { type FC, useState } from "react";
import {
  Controller,
  type FieldError,
  type FieldErrorsImpl,
  FormProvider,
  type Merge,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { fromISO8601_DATE, toISO8601_DATE } from "../../../utils/iso8601";
import { uuidv4 } from "../../../utils/uuid";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { eventsAtom } from "../../moment/types/store";
import type { Segment } from "../../segment/types";
import { TimeSelector } from "./time-selector";
import { TimeZoneSelector } from "./time-zone-selector";

export type OneOffFormProps = FormControlProps & {
  showValidationErrors: boolean;
  disabled: boolean;
};

export const OneOffForm: FC<OneOffFormProps> = ({ showValidationErrors, disabled }) => {
  const [errorToDisplay] = useState<string>();
  const methods = useFormContext<MomentFormMoment>();
  const today = new Date();
  const setEvents = useSetAtom(eventsAtom);

  const {
    control,
    setValue,
    getValues,
    formState: { touchedFields, errors },
  } = methods;

  useWatch({
    control,
    name: ["schedule.event", "target_channel"],
  });
  const [event, targetChannel] = getValues(["schedule.event", "target_channel"]);

  const truncateSeconds = (time: string): string => {
    const parts = time.split(":");
    return parts.slice(0, 2).join(":");
  };

  const dateChange = (newDate: Date | null): void => {
    const iso8601_date = toISO8601_DATE(newDate);
    if (!iso8601_date) {
      setValue("schedule.event", null);
      return;
    }

    const newEvent = {
      key: event ? event.key : `date-only-${uuidv4()}`,
      title: "",
      calendar: true,
      date: iso8601_date,
      library: false,
      repeat: null,
      number_of_repeats: 0,
      allow_initial: true,
      allow_repeats: false,
      has_schedule_event: false,
    };
    setEvents((events_) => [...events_, newEvent]);
    setValue("schedule.event", newEvent);
  };

  if (errorToDisplay) return <Alert severity="error">{errorToDisplay}</Alert>;
  return (
    <FormProvider {...methods}>
      <Grid container spacing={2} sx={{ width: 400 }}>
        <Grid size={{ xs: 12 }}>
          <DatePicker
            value={event?.date ? fromISO8601_DATE(event?.date) : null}
            minDate={today}
            onChange={(value) => dateChange(value)}
            slotProps={{
              textField: {
                id: "event-date-input",
                sx: { mr: 2 },
              },
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Controller
            name="schedule.send_at"
            control={control}
            render={({ field, fieldState }) => (
              <TimeSelector
                data-testid="one-off-date-picker"
                error={Boolean(showValidationErrors && fieldState.error)}
                sx={{ width: 130, paddingLeft: 1 }}
                value={truncateSeconds(field.value)}
                onChange={field.onChange}
                disabled={disabled}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 8 }}>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <TimeZoneSelector
                value={field.value}
                onChangeTimeZone={field.onChange}
                disabled={disabled}
                targetChannel={targetChannel}
              />
            )}
          />
        </Grid>
      </Grid>
      {showValidationErrors ||
      (typeof touchedFields.schedule !== "boolean" &&
        (touchedFields.schedule?.event || touchedFields.schedule?.send_at))
        ? uniqBy(
            Object.entries(errors.schedule ?? {})
              .filter(([k, v]) => v && !["events", "on_event_key"].includes(k))
              .map(([, v]) => v as Merge<FieldError, FieldErrorsImpl<Segment>>),
            "message",
          ).map((error, index) => <ErrMessage key={index} message={error.message} />)
        : null}
    </FormProvider>
  );
};

const ErrMessage: FC<{
  message?:
    | string
    | FieldError
    | Partial<{ type: string | number; message: string }>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Merge<FieldError, FieldErrorsImpl<any>>;
}> = ({ message }) => {
  if (!message) {
    return null;
  }

  if (typeof message === "string") {
    return (
      <FormHelperText error variant="filled">
        {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
        <span dangerouslySetInnerHTML={{ __html: message }} />
      </FormHelperText>
    );
  }

  if (message.message) return <ErrMessage message={message.message} />;

  return null;
};
